import React, { useCallback } from 'react';
import { FocusOn } from 'react-focus-on';
import { makeStyles } from 'tss-react/mui';
import { isElementInTheViewport } from '../../../base/ui/functions.web';
import { DRAWER_MAX_HEIGHT, DRAWER_MAX_HEIGHT_FALLBACK } from '../../constants';
const useStyles = makeStyles()(theme => {
    return {
        '@keyframes drawerSlideUp': {
            from: {
                opacity: 0,
                transform: 'translateY(100%)'
            },
            to: {
                opacity: 1,
                transform: 'translateY(0)'
            }
        },
        '@keyframes drawerBackdropIn': {
            from: {
                opacity: 0
            },
            to: {
                opacity: 1
            }
        },
        drawerMenuContainer: {
            alignItems: 'flex-end',
            animation: '$drawerBackdropIn 0.22s ease-out',
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            height: '100dvh'
        },
        drawer: {
            animation: '$drawerSlideUp 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
            backgroundColor: theme.palette.drawerBackground,
            borderRadius: '20px 20px 0 0',
            boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.18)',
            display: 'flex',
            flexDirection: 'column',
            marginBottom: 'env(safe-area-inset-bottom, 0)',
            maxHeight: `calc(${DRAWER_MAX_HEIGHT_FALLBACK})`,
            minHeight: 0,
            overflow: 'hidden',
            transformOrigin: 'bottom center',
            width: '100%',
            '@supports (height: 100dvh)': {
                maxHeight: `calc(${DRAWER_MAX_HEIGHT})`
            },
            '& .overflow-menu': {
                display: 'flex',
                flex: '1 1 auto',
                flexDirection: 'column',
                fontSize: '1.2em',
                listStyleType: 'none',
                margin: 'auto',
                minHeight: 0,
                overflow: 'hidden',
                padding: 0,
                '& .overflow-menu-item': {
                    boxSizing: 'border-box',
                    height: '48px',
                    padding: '12px 16px',
                    alignItems: 'center',
                    color: theme.palette.overflowMenuItemText,
                    cursor: 'pointer',
                    display: 'flex',
                    fontSize: '1rem',
                    '& div': {
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        '& svg': {
                            fill: theme.palette.overflowMenuItemIcon
                        }
                    },
                    '& > svg': {
                        fill: theme.palette.overflowMenuItemIcon
                    },
                    '@media (hover: hover) and (pointer: fine)': {
                        '&:hover': {
                            backgroundColor: theme.palette.overflowMenuItemHover,
                            '& svg': {
                                fill: theme.palette.overflowMenuItemIcon
                            }
                        }
                    },
                    '&.disabled': {
                        cursor: 'initial',
                        color: theme.palette.overflowMenuItemDisabled,
                        '& svg': {
                            fill: theme.palette.overflowMenuItemDisabled
                        }
                    }
                }
            }
        }
    };
});
/**
 * Component that displays the mobile friendly drawer on web.
 *
 * @returns {ReactElement}
 */
function Drawer({ children, className = '', headingId, isOpen, onClose }) {
    const { classes, cx } = useStyles();
    /**
     * Handles clicks within the menu, preventing the propagation of the click event.
     *
     * @param {Object} event - The click event.
     * @returns {void}
     */
    const handleInsideClick = useCallback(event => {
        event.stopPropagation();
    }, []);
    /**
     * Handles clicks outside of the menu, closing it, and also stopping further propagation.
     *
     * @param {Object} event - The click event.
     * @returns {void}
     */
    const handleOutsideClick = useCallback(event => {
        event.stopPropagation();
        onClose?.();
    }, [onClose]);
    /**
     * Handles pressing the escape key, closing the drawer.
     *
     * @param {KeyboardEvent<HTMLDivElement>} event - The keydown event.
     * @returns {void}
     */
    const handleEscKey = useCallback((event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onClose?.();
        }
    }, [onClose]);
    return (isOpen ? (React.createElement("div", { className: classes.drawerMenuContainer, onClick: handleOutsideClick, onKeyDown: handleEscKey },
        React.createElement("div", { className: cx(classes.drawer, className), onClick: handleInsideClick },
            React.createElement(FocusOn, { noIsolation: true, returnFocus: 
                // If we return the focus to an element outside the viewport the page will scroll to
                // this element which in our case is undesirable and the element is outside of the
                // viewport on purpose (to be hidden). For example if we return the focus to the toolbox
                // when it is hidden the whole page will move up in order to show the toolbox. This is
                // usually followed up with displaying the toolbox (because now it is on focus) but
                // because of the animation the whole scenario looks like jumping large video.
                isElementInTheViewport, scrollLock: false },
                React.createElement("div", { "aria-labelledby": headingId ? `#${headingId}` : undefined, "aria-modal": true, "data-autofocus": true, role: 'dialog', tabIndex: -1 }, children))))) : null);
}
export default Drawer;
